import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, IndianRupee, ArrowRight, Info, Zap, Package, TrendingUp, Sparkles, Flame } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PublicLayout } from './PublicLayout';
import { api } from '../../lib/api';

// 2026-07 pricing refresh: base plan ₹399/150 scans + two add-on packs.

const INCLUDED = [
    '150 bill scans per month',
    'Photo + PDF uploads (multi-page supported)',
    'GST-inclusive price-hike alerts',
    'Vendor payments & staff salary tracking',
    'Universal expenses (fixed + variable)',
    'Sales invoicing with GSTIN-compliant PDFs',
    'Excel workbook exports (.xlsx)',
    'Tally Prime XML exports',
    'GST summary + Vendor statements (PDF)',
    'PAN/GSTIN-anchored workspace',
    'Bank-grade security (rate limits, audit log)',
    'Priority email support',
    'Cancel anytime — no lock-in',
];

const PACKS = [
    {
        code: 'pack_25',
        scans: 25,
        amount: 100,
        perScan: 4,
        subtitle: 'Small top-up',
        note: 'Perfect when you have a busy week.',
        icon: Zap,
    },
    {
        code: 'pack_100',
        scans: 100,
        amount: 300,
        perScan: 3,
        subtitle: 'Best value — 25% off',
        note: '4× more scans for only 3× the price.',
        icon: Package,
        featured: true,
    },
];

export default function PricingPage() {
    const [founding, setFounding] = useState(null);
    useEffect(() => {
        api.foundingStatus().then(setFounding).catch(() => setFounding(null));
    }, []);
    const foundingActive = !!founding?.active;
    const slotsRemaining = founding?.slots_remaining ?? 0;
    const slotsUsed = founding?.slots_used ?? 0;
    const slotsTotal = founding?.slots_total ?? 100;
    const pctFilled = slotsTotal ? Math.min(100, Math.round((slotsUsed / slotsTotal) * 100)) : 0;

    return (
        <PublicLayout>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
                {/* Founding-100 banner — only shown while slots remain */}
                {foundingActive && (
                    <div
                        className="mb-10 rounded-2xl border-2 border-primary bg-gradient-to-br from-primary/[0.08] via-background to-primary/[0.04] p-6 sm:p-8 relative overflow-hidden"
                        data-testid="founding-banner"
                    >
                        <div className="absolute top-4 right-4 hidden sm:flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1">
                            <Flame size={11} /> Limited time
                        </div>
                        <div className="grid md:grid-cols-[1.3fr_1fr] gap-6 items-center">
                            <div>
                                <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold px-2.5 py-1 mb-3">
                                    <Sparkles size={12} /> Founding 100 offer
                                </div>
                                <h2 className="font-heading text-2xl sm:text-3xl mb-2 leading-tight">
                                    ₹249/month <span className="text-muted-foreground">for life.</span>
                                </h2>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Be one of our first 100 subscribers and lock in ₹249/month <span className="font-semibold text-foreground">forever</span> —
                                    even after we raise prices. Same 150 scans, same features. No catches.
                                </p>
                                <div className="flex items-baseline gap-3 mb-3 flex-wrap">
                                    <div className="flex items-baseline gap-1">
                                        <IndianRupee size={18} className="text-foreground" />
                                        <span className="font-heading text-4xl">249</span>
                                        <span className="text-muted-foreground text-sm">/mo</span>
                                    </div>
                                    <div className="text-sm text-muted-foreground line-through">₹399/mo</div>
                                    <div className="text-xs bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 rounded-full px-2 py-0.5 font-medium">
                                        Save 38% forever
                                    </div>
                                </div>
                                <Button asChild size="lg" className="gap-2" data-testid="founding-cta">
                                    <Link to="/login?tab=signup">
                                        Claim my founding seat <ArrowRight size={16} />
                                    </Link>
                                </Button>
                            </div>
                            <div className="rounded-xl border border-primary/30 bg-background/70 backdrop-blur-sm p-5" data-testid="founding-counter">
                                <div className="flex items-baseline justify-between mb-2">
                                    <div>
                                        <div className="font-heading text-3xl leading-none tabular-nums">{slotsRemaining}</div>
                                        <div className="text-xs text-muted-foreground mt-1">of {slotsTotal} founding seats left</div>
                                    </div>
                                    <div className="text-right text-[11px] text-muted-foreground">
                                        {slotsUsed} claimed
                                    </div>
                                </div>
                                <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                                    <div
                                        className="h-full bg-primary transition-all"
                                        style={{ width: `${pctFilled}%` }}
                                        aria-label={`${pctFilled}% of founding seats claimed`}
                                    />
                                </div>
                                <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
                                    Once these 100 seats are gone, standard pricing (₹399/mo) applies. Founding members keep ₹249/mo permanently, even if we raise prices later.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Header */}
                <div className="text-center max-w-2xl mx-auto mb-12">
                    <div className="text-xs uppercase tracking-wider text-primary font-semibold mb-2">Standard Pricing</div>
                    <h1 className="font-heading text-4xl sm:text-5xl mb-4" data-testid="pricing-title">
                        Simple pricing. Made in India.
                    </h1>
                    <p className="text-muted-foreground">
                        One plan. Everything included. 1-month free trial to be sure.
                    </p>
                </div>

                {/* Main plan grid */}
                <div className="grid lg:grid-cols-[1fr_auto_1fr] gap-8 items-start">
                    {/* Left: value list */}
                    <div className="order-2 lg:order-1">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">What&apos;s included</div>
                        <ul className="space-y-2" data-testid="pricing-included">
                            {INCLUDED.map((item, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm">
                                    <span className="mt-0.5 h-4 w-4 rounded-full bg-primary/10 text-primary grid place-items-center flex-shrink-0">
                                        <Check size={11} />
                                    </span>
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Center: price card */}
                    <div className="order-1 lg:order-2 relative w-full lg:w-[380px]">
                        <div className="absolute -inset-3 bg-gradient-to-br from-primary/20 via-transparent to-primary/10 rounded-3xl blur-2xl" aria-hidden />
                        <div className="relative rounded-2xl border-2 border-primary bg-background p-8 shadow-lg" data-testid="pricing-card">
                            <div className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-xs font-semibold px-3 py-1 mb-4">
                                MOST POPULAR
                            </div>
                            <div className="font-heading text-2xl mb-1">Ease My Bill Pro</div>
                            <div className="text-sm text-muted-foreground mb-6">Everything a small business needs.</div>
                            <div className="flex items-baseline gap-1 mb-1">
                                <IndianRupee size={26} className="text-foreground" />
                                <span className="font-heading text-5xl" data-testid="pricing-amount">399</span>
                                <span className="text-muted-foreground">/month</span>
                            </div>
                            <div className="text-xs text-muted-foreground mb-6" data-testid="pricing-per-scan">
                                ≈ ₹2.66 per bill scan · 150 scans included
                            </div>
                            <div className="inline-flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5 mb-6" data-testid="pricing-trial-badge">
                                <Check size={12} /> 1 month free trial
                            </div>
                            <Button asChild className="w-full gap-2 mb-3" size="lg" data-testid="pricing-cta">
                                <Link to="/login?tab=signup">Start free trial <ArrowRight size={16} /></Link>
                            </Button>
                            <p className="text-xs text-muted-foreground text-center">
                                No credit card required to start. First charge only after 30 days.
                            </p>
                        </div>
                    </div>

                    {/* Right: fine-print / FAQ */}
                    <div className="order-3">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Fine print</div>
                        <div className="space-y-4 text-sm" data-testid="pricing-fineprint">
                            <div>
                                <div className="font-heading text-base mb-1">What is a &ldquo;scan&rdquo;?</div>
                                <p className="text-muted-foreground">Each successfully processed bill counts as one scan. A multi-bill PDF that splits into 3 bills = 3 scans.</p>
                            </div>
                            <div>
                                <div className="font-heading text-base mb-1">More than 150/month?</div>
                                <p className="text-muted-foreground">Grab a top-up pack below (never expire), or email <a className="text-primary hover:underline" href="mailto:support@easemybill.com">support</a> for a custom plan.</p>
                            </div>
                            <div>
                                <div className="font-heading text-base mb-1">Refunds?</div>
                                <p className="text-muted-foreground">7-day money-back guarantee. See our <Link to="/refund" className="text-primary hover:underline">Refund Policy</Link>.</p>
                            </div>
                            <div>
                                <div className="font-heading text-base mb-1">Payment methods</div>
                                <p className="text-muted-foreground">Card, UPI, netbanking — powered by Razorpay. We never see your card details.</p>
                            </div>
                            <div className="rounded-lg border border-border/60 bg-muted/30 p-3 flex gap-2 text-xs text-muted-foreground">
                                <Info size={14} className="flex-shrink-0 mt-0.5 text-primary" />
                                <span>Prices are inclusive of applicable GST. You&apos;ll receive a GST invoice for every charge.</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Add-on scan packs */}
                <div className="mt-16" data-testid="scan-packs-section">
                    <div className="text-center max-w-2xl mx-auto mb-8">
                        <div className="text-xs uppercase tracking-wider text-primary font-semibold mb-2">Top-up packs</div>
                        <h2 className="font-heading text-3xl sm:text-4xl mb-3">Need more scans?</h2>
                        <p className="text-muted-foreground">
                            One-time top-ups that stack on top of your monthly cap. <span className="font-medium text-foreground">They never expire.</span>
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
                        {PACKS.map((p) => (
                            <div
                                key={p.code}
                                className={`relative rounded-2xl border p-6 bg-background ${p.featured ? 'border-primary border-2 shadow-md' : 'border-border/60'}`}
                                data-testid={`scan-pack-${p.code}`}
                            >
                                {p.featured && (
                                    <div className="absolute -top-2.5 left-6 inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5">
                                        <TrendingUp size={10} /> Best value
                                    </div>
                                )}
                                <div className="flex items-start gap-3 mb-4">
                                    <div className={`h-10 w-10 rounded-lg grid place-items-center shrink-0 ${p.featured ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'}`}>
                                        <p.icon size={18} />
                                    </div>
                                    <div>
                                        <div className="font-heading text-xl">{p.scans} scans</div>
                                        <div className="text-xs text-muted-foreground">{p.subtitle}</div>
                                    </div>
                                    <div className="ml-auto text-right">
                                        <div className="flex items-baseline gap-0.5">
                                            <IndianRupee size={14} className="text-foreground" />
                                            <span className="font-heading text-2xl">{p.amount}</span>
                                        </div>
                                        <div className="text-[10px] text-muted-foreground">₹{p.perScan}/scan</div>
                                    </div>
                                </div>
                                <p className="text-xs text-muted-foreground mb-4">{p.note}</p>
                                <Button
                                    asChild
                                    variant={p.featured ? 'default' : 'outline'}
                                    className="w-full"
                                    data-testid={`scan-pack-cta-${p.code}`}
                                >
                                    <Link to={`/login?tab=signup&pack=${p.code}`}>
                                        Start with this pack
                                    </Link>
                                </Button>
                            </div>
                        ))}
                    </div>
                    <p className="text-xs text-muted-foreground text-center mt-4">
                        Packs are purchased after signup from Settings → Billing → Top up. Payment via Razorpay.
                    </p>
                </div>
            </div>
        </PublicLayout>
    );
}
